import { AfterContentInit, ChangeDetectorRef, Component, ContentChildren, ElementRef, EventEmitter, Input, OnDestroy, Output, QueryList, forwardRef, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

/** One choice of <app-select> (replacement for mat-option). Value can be any type. */
@Component({ selector: 'app-option', standalone: false, template: '<ng-content></ng-content>' })
export class AppOptionComponent {
  @Input() value: unknown;
  @Input() disabled = false;
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  get label(): string {
    return (this.host.nativeElement.textContent ?? '').trim();
  }
}

/**
 * Select on the OneVedAI select (replacement for mat-select). Options are <app-option [value]> children; values may be
 * any type. `multiple` renders a checkbox list. Works with formControlName, [formControl] and [(ngModel)].
 */
@Component({
  selector: 'app-select',
  standalone: false,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AppSelectComponent), multi: true }],
  template: `
    @if (searchable && !multiple) {
      <div class="combo" (focusin)="openPanel()" (focusout)="closePanel($event)" (keydown.escape)="open = false" (keydown.enter)="pickFirst($event)">
        <onevedai-input
          type="text"
          autocomplete="off"
          [label]="label"
          [placeholder]="selectedLabel || placeholder || 'Search...'"
          [value]="open ? query : selectedLabel"
          [disabled]="disabled"
          [invalid]="invalid"
          [errorMessage]="errorMessage"
          (valueChange)="onQuery($event)"
        ></onevedai-input>
        @if (open) {
          <ul class="panel" role="listbox">
            @if (tooShort) {
              <li class="empty">Type at least {{ minChars }} characters to search</li>
            } @else if (busy) {
              <li class="empty">Searching...</li>
            } @else {
              @for (o of shown; track o.index) {
                <li role="option" [class.selected]="o.index === selectedIndex" [class.disabled]="o.disabled" [attr.aria-selected]="o.index === selectedIndex" (mousedown)="pick(o.index, $event)">{{ o.label }}</li>
              } @empty {
                <li class="empty">No matches</li>
              }
            }
            @if (allowCreate && canCreate && !busy) {
              <li class="create" (mousedown)="create($event)"><span class="material-symbols-outlined">add</span>Create "{{ query.trim() }}"</li>
            }
            @if (hiddenCount > 0) {
              <li class="empty">{{ hiddenCount }} more. Keep typing to narrow the list.</li>
            }
          </ul>
        }
      </div>
    } @else if (multiple) {
      <div class="multi" role="group">
        @if (label) {
          <span class="multi-label">{{ label }}</span>
        }
        @for (o of optionList; track $index) {
          <label class="multi-item">
            <onevedai-checkbox [checked]="isSelected(o.value)" [disabled]="disabled || o.disabled" (checkedChange)="toggleMulti(o.value, $event)"></onevedai-checkbox>
            <span>{{ o.label }}</span>
          </label>
        }
      </div>
    } @else {
      <onevedai-select
        [label]="label"
        [placeholder]="placeholder"
        [value]="selectedKey"
        [disabled]="disabled"
        [invalid]="invalid"
        [errorMessage]="errorMessage"
        [hint]="hint"
        (valueChange)="onSelect($event)"
        (focusout)="onTouched()"
      >
        @for (o of optionList; track $index) {
          <onevedai-option [value]="'' + $index" [disabled]="!!o.disabled">{{ o.label }}</onevedai-option>
        }
      </onevedai-select>
    }
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .combo {
        position: relative;
      }
      .panel {
        position: absolute;
        z-index: 30;
        left: 0;
        right: 0;
        margin: 4px 0 0;
        padding: var(--space-1);
        max-height: 260px;
        overflow-y: auto;
        list-style: none;
        background: hsl(var(--md-surface-bright));
        border: 1px solid hsl(var(--border));
        border-radius: var(--radius-md);
        box-shadow: var(--shadow-lg);
        font: var(--text-body-md);
        color: hsl(var(--md-on-surface));
      }
      .panel li {
        padding: var(--space-2) var(--space-3);
        border-radius: var(--radius-md);
        cursor: pointer;
      }
      .panel li:hover:not(.empty):not(.disabled),
      .panel li.selected {
        background: hsl(var(--md-primary) / 0.08);
      }
      .panel li.create {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        font-weight: 600;
        color: hsl(var(--md-primary));
      }
      .panel li.disabled,
      .panel li.empty {
        cursor: default;
        color: hsl(var(--md-on-surface-variant));
      }
      .multi {
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
      }
      .multi-label {
        font: var(--text-label-sm);
        letter-spacing: var(--tracking-label-sm);
        text-transform: uppercase;
        color: var(--text-muted);
      }
      .multi-item {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        font: var(--text-body-md);
      }
    `,
  ],
})
export class AppSelectComponent implements ControlValueAccessor, AfterContentInit, OnDestroy {
  @Input() label = '';
  @Input() placeholder = '';
  @Input() hint = '';
  @Input() multiple = false;
  /** Type to filter the options by their text (single select only). Use for long lists. */
  @Input() searchable = false;
  /** With `searchable`: offers "Create <typed text>" when nothing matches exactly, and emits `createNew` with the text. */
  @Input() allowCreate = false;
  @Output() createNew = new EventEmitter<string>();
  /**
   * With `searchable`: the options come from the server instead of being filtered here. Nothing is asked until at least
   * `minChars` characters are typed and typing has paused; then `search` emits the text and the parent fills the options.
   * The parent sets `loading` while its request runs.
   */
  @Input() remote = false;
  @Input() minChars = 3;
  @Input() set loading(v: boolean) {
    this.isLoading = v;
    if (!v) this.waiting = false;
  }
  @Output() search = new EventEmitter<string>();
  private isLoading = false;
  /** Typed enough, but the search has not been sent or answered yet. */
  private waiting = false;

  get busy(): boolean {
    return this.remote && (this.isLoading || this.waiting);
  }

  get tooShort(): boolean {
    return this.remote && this.query.trim().length < this.minChars;
  }
  @Input() invalid = false;
  @Input() errorMessage = '';
  @Input() disabled = false;
  @Input() required = false;
  /** Writable binding as an alternative to a form control. */
  @Input() set value(v: unknown) {
    this.writeValue(v);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() valueChange = new EventEmitter<any>();
  /** MatSelect API compatibility: `(selectionChange)="f($event.value)"`. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() selectionChange = new EventEmitter<{ value: any }>();

  @ContentChildren(AppOptionComponent, { descendants: true }) options!: QueryList<AppOptionComponent>;
  private sub?: Subscription;
  private readonly cdr = inject(ChangeDetectorRef);

  private current: unknown = null;
  private onChange: (v: unknown) => void = () => undefined;
  onTouched: () => void = () => undefined;

  get optionList(): AppOptionComponent[] {
    return this.options?.toArray() ?? [];
  }

  /** Index (as string) of the option holding the current value, '' when none. */
  get selectedKey(): string {
    const i = this.optionList.findIndex(
      (o) => o.value === this.current || (o.value != null && this.current != null && String(o.value) === String(this.current)),
    );
    return i < 0 ? '' : String(i);
  }

  // ---- searchable mode -------------------------------------------------------------------------------------
  open = false;
  query = '';
  private readonly maxShown = 100;

  get selectedIndex(): number {
    return this.selectedKey === '' ? -1 : Number(this.selectedKey);
  }

  get selectedLabel(): string {
    return this.optionList[this.selectedIndex]?.label ?? '';
  }

  /** The options matching the text typed so far, worked out once per (debounced) change rather than on every render. */
  shown: { index: number; label: string; disabled: boolean }[] = [];
  hiddenCount = 0;
  private readonly typed$ = new Subject<string>();

  onQuery(text: string): void {
    this.query = text;
    this.open = true;
    this.waiting = this.remote && text.trim().length >= this.minChars;
    this.typed$.next(text);
  }

  private refresh(text = this.query): void {
    const q = text.trim().toLowerCase();
    // remote options are already the answer to the typed text; only the selected one (kept in the list to show its name) is still matched here
    const all = this.optionList
      .map((o, index) => ({ index, label: o.label, disabled: o.disabled }))
      .filter((o) => !q || o.label.toLowerCase().includes(q) || (this.remote && o.index !== this.selectedIndex));
    this.shown = all.slice(0, this.maxShown);
    this.hiddenCount = Math.max(0, all.length - this.maxShown);
  }

  get canCreate(): boolean {
    const q = this.query.trim().toLowerCase();
    return !!q && !this.optionList.some((o) => o.label.toLowerCase() === q);
  }

  create(event: Event): void {
    event.preventDefault();
    this.createNew.emit(this.query.trim());
    this.open = false;
  }

  openPanel(): void {
    if (this.disabled || this.open) return;
    this.query = '';
    this.refresh();
    this.open = true;
  }

  closePanel(event: FocusEvent): void {
    if (!(event.currentTarget as HTMLElement).contains(event.relatedTarget as Node | null)) {
      this.open = false;
      this.onTouched();
    }
  }

  /** mousedown, so the input keeps focus and the panel does not close before the pick lands. */
  pick(index: number, event: Event): void {
    event.preventDefault();
    if (this.optionList[index]?.disabled) return;
    this.onSelect(String(index));
    this.open = false;
  }

  pickFirst(event: Event): void {
    const first = this.shown.find((o) => !o.disabled);
    if (this.open && !this.tooShort && !this.busy && first) this.pick(first.index, event);
  }

  ngAfterContentInit(): void {
    this.sub = this.options.changes.subscribe(() => {
      if (this.open) this.refresh();
      this.cdr.markForCheck();
    });
    // filtering waits until typing pauses, so a long list is not re-filtered on every key
    this.sub.add(
      this.typed$.pipe(debounceTime(this.remote ? 350 : 200)).subscribe((text) => {
        if (this.remote && text.trim().length >= this.minChars) this.search.emit(text.trim());
        this.refresh(text);
        this.cdr.markForCheck();
      }),
    );
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onSelect(key: string): void {
    const option = this.optionList[Number(key)];
    this.commit(option ? option.value : null);
  }

  isSelected(value: unknown): boolean {
    return Array.isArray(this.current) && this.current.some((v) => v === value || String(v) === String(value));
  }

  toggleMulti(value: unknown, checked: boolean): void {
    const list = Array.isArray(this.current) ? [...this.current] : [];
    this.commit(checked ? [...list, value] : list.filter((v) => !(v === value || String(v) === String(value))));
  }

  private commit(value: unknown): void {
    this.current = value;
    this.onChange(value);
    this.valueChange.emit(value);
    this.selectionChange.emit({ value });
  }

  writeValue(value: unknown): void {
    this.current = value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (v: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
